import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {catchError, concatMap, map, of, switchMap, withLatestFrom} from 'rxjs';
import {tap} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {CatalogCreateRequest, DEFAULT_CATALOGS_QUERY_PARAMS} from '../../../models/clare';
import {CatalogApiService} from '../../../services/catalog-api.service';
import {RegistrationApiService} from '../../../services/registration-api.service';
import {selectSelectedOrganizationId} from '../../../state/router/router.selectors';
import {CatalogActions} from './catalog.actions';
import {MISSING_CATALOG_ERROR} from './catalog.constants';

@Injectable()
export class CatalogEffects {
  loadCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.loadCatalogs),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([{queryParams}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogActions.loadCatalogsFailure({
              error: {message: 'Failed to load the organization catalogs, please refresh the page.'},
            })
          );
        }

        return this.catalogApi.getCatalogsByOrganizationId(organizationId, queryParams).pipe(
          map(catalogs => CatalogActions.loadCatalogsSuccess({catalogs})),
          catchError(() =>
            of(
              CatalogActions.loadCatalogsFailure({
                error: {message: 'Failed to load the organization catalogs, please refresh the page.'},
              })
            )
          )
        );
      })
    )
  );

  createCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.createCatalog),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      concatMap(([{catalog}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogActions.createCatalogFailure({
              error: {message: 'Failed to create the catalog, please refresh the page and try again.'},
            })
          );
        }

        const catalogRequest: CatalogCreateRequest = {
          ...catalog,
          organization_id: organizationId,
        };

        return this.catalogApi.create(catalogRequest).pipe(
          map(catalog => CatalogActions.createCatalogSuccess({catalog})),
          catchError(() =>
            of(
              CatalogActions.createCatalogFailure({
                error: {message: 'Failed to create the catalog, please refresh the page and try again.'},
              })
            )
          )
        );
      })
    )
  );

  createCatalogSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.createCatalogSuccess),
        tap(() => {
          this.toast.done('Successfully created catalog');
        })
      ),
    {dispatch: false}
  );

  createCatalogFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.createCatalogFailure),
        tap(({error}) => {
          this.toast.critical(error.message);
        })
      ),
    {dispatch: false}
  );

  deleteCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.deleteCatalog),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      concatMap(([{catalogId}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogActions.deleteCatalogFailure({
              catalogId,
              error: {message: 'Failed to delete the catalog, please refresh the page and try again.'},
            })
          );
        }

        return this.catalogApi.delete(organizationId, catalogId).pipe(
          map(() => CatalogActions.deleteCatalogSuccess({catalogId})),
          catchError(() =>
            of(
              CatalogActions.deleteCatalogFailure({
                catalogId,
                error: {message: 'Failed to delete the catalog, please refresh the page and try again.'},
              })
            )
          )
        );
      })
    )
  );

  reloadCatalogsAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.deleteCatalogSuccess),
      map(() => CatalogActions.loadCatalogs({queryParams: DEFAULT_CATALOGS_QUERY_PARAMS}))
    )
  );

  deleteCatalogSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.deleteCatalogSuccess),
        tap(() => this.toast.done('Catalog deleted'))
      ),
    {dispatch: false}
  );

  deleteCatalogFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.deleteCatalogFailure),
        tap(({error}) => this.toast.critical(error.message))
      ),
    {dispatch: false}
  );

  loadCatalogRegistrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.loadCatalogRegistrations),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([{catalogId, catalogKey}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogActions.loadCatalogRegistrationsFailure({
              catalogId,
              error: {message: 'Failed to load catalog registrations, please refresh the page and try again.'},
            })
          );
        }

        return this.registrationApi.getByCatalogKey(organizationId, catalogKey).pipe(
          map(registrations => CatalogActions.loadCatalogRegistrationsSuccess({catalogId, registrations})),
          catchError(() =>
            of(
              CatalogActions.loadCatalogRegistrationsFailure({
                catalogId,
                error: {message: 'Failed to load catalog registrations, please refresh the page and try again.'},
              })
            )
          )
        );
      })
    )
  );

  loadCatalogsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.loadCatalogsFailure),
        tap(({error}) => {
          this.router.navigateByUrl('/error', {
            state: {
              error: {
                ...error,
                code: error.code ?? MISSING_CATALOG_ERROR.error.code,
              },
            },
          });
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private catalogApi: CatalogApiService,
    private registrationApi: RegistrationApiService,
    private router: Router,
    private toast: SeFeToastService
  ) {}
}
